import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, BookOpen, Clock, ChevronRight, Sparkles } from "lucide-react";
import type { SolutionData } from "@/lib/solve";

export const Route = createFileRoute("/history")({
  component: HistoryPage,
  head: () => ({
    meta: [
      { title: "Solved Problems - Inquira" },
      { name: "description", content: "Browse your previously solved problems organized by subject." },
      { property: "og:title", content: "Solved Problems - Inquira" },
      { property: "og:description", content: "Browse your previously solved problems organized by subject." },
      { property: "og:url", content: "https://www.inquira.online/history" },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/history" }],
  }),
});

interface SolvedProblem {
  id: string;
  question: string;
  question_type: string;
  concept: string | null;
  final_answer: string | null;
  solution: SolutionData;
  created_at: string;
}

function HistoryPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [problems, setProblems] = useState<SolvedProblem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const fetchProblems = async () => {
      const { data } = await supabase
        .from("solved_problems")
        .select("id, question, question_type, concept, final_answer, solution, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      setProblems((data as unknown as SolvedProblem[]) ?? []);
      setLoading(false);
    };
    fetchProblems();
  }, [user]);

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <BookOpen className="size-12 text-ink-muted mx-auto" />
          <p className="text-ink-muted">Sign in to view your solved problems</p>
          <Link to="/login" className="text-neon-pink font-semibold">Sign In</Link>
        </div>
      </div>
    );
  }

  const concepts = [...new Set(problems.map(p => p.concept || "Uncategorized"))];
  const filtered = selectedConcept
    ? problems.filter(p => (p.concept || "Uncategorized") === selectedConcept)
    : problems;

  return (
    <div className="min-h-screen notebook-lines">
      <div className="max-w-lg mx-auto px-4 pb-24">
        <header className="flex items-center gap-3 py-4">
          <Link to="/" aria-label="Back to home" title="Back to home" className="size-9 rounded-full bg-card border border-rule flex items-center justify-center hover:bg-rule/30 transition-colors">
            <ArrowLeft className="size-4 text-ink-muted" />
          </Link>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-xl text-ink">
            Solved Problems
          </h1>
          <span className="ml-auto text-sm text-ink-muted font-medium">{problems.length} total</span>
        </header>

        {concepts.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
            <button
              onClick={() => setSelectedConcept(null)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                !selectedConcept
                  ? "bg-neon-pink text-paper"
                  : "bg-card border border-rule text-ink-muted hover:text-ink"
              }`}
            >
              All
            </button>
            {concepts.map(c => (
              <button
                key={c}
                onClick={() => setSelectedConcept(c)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  selectedConcept === c
                    ? "bg-neon-pink text-paper"
                    : "bg-card border border-rule text-ink-muted hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {concepts.some((c) => /calculus|derivative|integral|limit|series|taylor/i.test(c)) && (
          <div className="mb-4 rounded-xl border border-rule bg-card p-3 text-xs text-ink-muted">
            You've been working on calculus.{" "}
            <Link to="/calculus-ai-solver" className="text-neon-pink font-semibold underline">
              Browse the Calculus AI Solver guide →
            </Link>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <div className="size-12 rounded-full bg-neon-pink/10 flex items-center justify-center animate-pulse">
              <Sparkles className="size-6 text-neon-pink" />
            </div>
            <p className="text-sm text-ink-muted">Loading your problems...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <BookOpen className="size-12 text-ink-muted/30 mx-auto" />
            <p className="text-ink-muted text-sm">No solved problems yet</p>
            <Link to="/" className="text-neon-pink text-sm font-semibold">Solve your first problem →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                className="w-full text-left bg-card rounded-2xl p-4 border border-rule shadow-sm hover:border-neon-pink/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-full bg-neon-pink/10 flex items-center justify-center shrink-0 mt-0.5">
                    <BookOpen className="size-4 text-neon-pink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{p.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {p.concept && (
                        <span className="text-xs bg-neon-blue/10 text-neon-blue px-2 py-0.5 rounded-full font-medium">
                          {p.concept}
                        </span>
                      )}
                      <span className="text-xs text-ink-muted flex items-center gap-1">
                        <Clock className="size-3" />
                        {new Date(p.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {p.final_answer && (
                      <p className="text-xs text-ink-muted mt-1.5">
                        Answer: <span className="font-[family-name:var(--font-mono)] font-bold text-ink">{p.final_answer}</span>
                      </p>
                    )}
                  </div>
                  <ChevronRight className={`size-4 text-ink-muted shrink-0 transition-transform ${expandedId === p.id ? "rotate-90" : ""}`} />
                </div>

                {expandedId === p.id && p.solution && (
                  <div className="mt-4 pt-4 border-t border-rule space-y-3">
                    {p.solution.conceptDescription && (
                      <p className="text-sm text-ink/80">{p.solution.conceptDescription}</p>
                    )}
                    {p.solution.steps?.map((step, i) => (
                      <div key={i} className="pl-3 border-l-2 border-neon-pink/30">
                        <p className="text-xs font-bold text-ink">{i + 1}. {step.title}</p>
                        <p className="text-xs text-ink-muted mt-0.5">{step.explanation}</p>
                        {step.work && (
                          <p className="text-xs font-[family-name:var(--font-mono)] text-ink/70 mt-1 bg-paper rounded px-2 py-1">{step.work}</p>
                        )}
                      </div>
                    ))}
                    <div className="bg-neon-yellow/20 rounded-xl p-3">
                      <p className="text-xs font-bold text-ink">Final Answer: {p.solution.finalAnswer}</p>
                      <p className="text-xs text-ink/70 mt-1">{p.solution.summary}</p>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
